import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { SortOrder } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const ENTERPRISE_ORGANIZATION_CHILD_USER_GROUPS_QUERY_KEY =
  'enterprise-organization-child-user-groups';

export const getUserGroupsQueryKey = (params: {
  enterpriseOrgId: string;
  search?: string;
  organizationId?: string[];
  pageLimit?: number;
  sortField?: string;
  sortOrder?: SortOrder;
}) => {
  if (params.pageLimit === undefined) {
    return [
      ENTERPRISE_ORGANIZATION_CHILD_USER_GROUPS_QUERY_KEY,
      params.enterpriseOrgId,
    ] as const;
  }

  return [
    ENTERPRISE_ORGANIZATION_CHILD_USER_GROUPS_QUERY_KEY,
    params.enterpriseOrgId,
    params.search,
    params.organizationId,
    params.pageLimit,
    params.sortField,
    params.sortOrder,
  ] as const;
};

type UserGroupsQueryParams = {
  enterpriseOrgId: string;
  search?: string;
  pageLimit?: number;
  organizationId?: string[];
  sortField?: string;
  sortOrder?: SortOrder;
};

const getUserGroupsQueryOptions = ({
  enterpriseOrgId,
  search,
  pageLimit = 50,
  organizationId,
  sortField,
  sortOrder,
  enabled = true,
}: UserGroupsQueryParams & { enabled?: boolean }) =>
  infiniteQueryOptions({
    queryKey: getUserGroupsQueryKey({
      enterpriseOrgId,
      search,
      organizationId,
      pageLimit,
      sortField,
      sortOrder,
    }),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.enterprises.listUserGroups(
        {
          enterpriseId: enterpriseOrgId,
          pageLimit: pageLimit.toString(),
          pageCursor: pageParam,
          organizationId,
          groupName: search,
          sortField,
          sortOrder,
        },
        {
          headers: overrideOrganizationHeader(enterpriseOrgId),
        }
      );

      return res.data;
    },
    initialPageParam: '0',
    maxPages: 1,
    getNextPageParam: ({ nextPageCursor }) =>
      nextPageCursor ? nextPageCursor : undefined,
    getPreviousPageParam: ({ prevPageCursor }) =>
      prevPageCursor ? prevPageCursor : undefined,
    enabled: makeIsEnabled(!!enterpriseOrgId, enabled),
  });

type UserGroupsQueryArgs = {
  enabled?: boolean;
  search?: string;
  pageLimit?: number;
  organizationId?: string[];
  sortField?: string;
  sortOrder?: SortOrder;
};

export const useUserGroupsInfiniteQuery = ({
  enabled,
  search,
  pageLimit,
  organizationId,
  sortField,
  sortOrder,
}: UserGroupsQueryArgs) => {
  const { enterpriseOrgId } = useOrganizations();

  return useInfiniteQuery(
    getUserGroupsQueryOptions({
      enterpriseOrgId: enterpriseOrgId as string,
      search,
      pageLimit,
      organizationId,
      sortField,
      sortOrder,
      enabled,
    })
  );
};
